import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Upload, Users, Package, BarChart3, TrendingUp, Sparkles,
  Headphones, Megaphone, Workflow, Settings, Menu, LogOut, ShieldCheck, Brain,
  ShieldAlert,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../../lib/auth";
import { api, fileUrl } from "../../lib/api";

// Every module is ALWAYS visible in the sidebar per the launch spec. If a
// module needs data the user hasn't imported yet, the module itself renders
// a polished empty state (see FeatureLocked / page-level empty states).
const NAV = [
  { to: "/dashboard",              label: "Dashboard",             icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/imports",                label: "Imports",               icon: Upload,          testid: "nav-imports" },
  { to: "/data-quality",           label: "Data Quality",          icon: ShieldCheck,     testid: "nav-data-quality" },
  { to: "/business-intelligence",  label: "Business Intelligence", icon: Brain,           testid: "nav-business-intelligence" },
  { to: "/customers",              label: "Customers",             icon: Users,           testid: "nav-customers" },
  { to: "/products",               label: "Products",              icon: Package,         testid: "nav-products" },
  { to: "/analytics",              label: "Analytics",             icon: BarChart3,       testid: "nav-analytics" },
  { to: "/opportunities",          label: "Opportunities",         icon: TrendingUp,      testid: "nav-opportunities" },
  { to: "/ai-command-center",      label: "AI Command Center",    icon: Sparkles,        testid: "nav-ai" },
  { to: "/crm",                    label: "CRM",                   icon: Headphones,      testid: "nav-crm" },
  { to: "/campaigns",              label: "Campaigns",             icon: Megaphone,       testid: "nav-campaigns" },
  { to: "/automation",             label: "Automation",            icon: Workflow,        testid: "nav-automation" },
  { to: "/settings/profile",       label: "Settings",              icon: Settings,        testid: "nav-settings" },
];

const ADMIN_EMAIL = "admin@milkath.com";

function useOrganization() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setOrg(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/organization");
        if (!cancelled) setOrg(data);
      } catch {
        if (!cancelled) setOrg(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);
  return org;
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const isAdmin = (user?.email || "").toLowerCase() === ADMIN_EMAIL;
  const navItems = isAdmin
    ? [...NAV.slice(0, -1),
       { to: "/admin", label: "Admin", icon: ShieldAlert, testid: "nav-admin" },
       NAV[NAV.length - 1]]
    : NAV;
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-zinc-800/80">
        <Link to="/dashboard" onClick={onNavigate}>
          <Logo />
        </Link>
      </div>
      <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin">
        <p className="px-3 mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Workspace</p>
        <ul className="space-y-0.5">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={onNavigate}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-zinc-800 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.6} />
                <span className="flex-1">{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="p-3 border-t border-zinc-800/80">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
          <div className="h-8 w-8 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-xs text-zinc-200">
            {user?.picture ? (
              <img src={fileUrl(user.picture)} alt="" className="h-full w-full object-cover" />
            ) : (
              user?.name?.charAt(0).toUpperCase()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-zinc-100 truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-zinc-500 capitalize">{user?.role}</div>
          </div>
          <button
            onClick={logout}
            className="p-2 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
            title="Sign out"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const org = useOrganization();
  const pageTitle = (() => {
    const item = NAV.find((n) => location.pathname.startsWith(n.to));
    if (location.pathname.startsWith("/settings")) return "Settings";
    if (location.pathname.startsWith("/admin")) return "Admin";
    return item?.label || "Milkath";
  })();

  // Header shows the workspace/organization NAME — never a raw ID.
  const workspaceName = org?.name || org?.business_name || "Workspace";

  return (
    <div className="flex min-h-screen bg-zinc-950 text-zinc-100">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-zinc-800/80 bg-zinc-950 flex-col fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-zinc-950 border-r border-zinc-800 flex flex-col">
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 glass border-b border-zinc-800/60 flex items-center px-4 lg:px-8 sticky top-0 z-20">
          <button
            className="lg:hidden mr-3 p-2 rounded-md text-zinc-300 hover:bg-zinc-800"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-base font-medium tracking-tight" data-testid="topbar-page-title">{pageTitle}</h1>
          <div className="ml-auto flex items-center gap-2 text-xs text-zinc-500">
            <span className="hidden sm:inline">Workspace</span>
            <span className="hidden sm:inline text-zinc-700">·</span>
            <span
              className="text-zinc-300 truncate max-w-[220px]"
              data-testid="topbar-workspace-name"
              title={workspaceName}
            >
              {workspaceName}
            </span>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 py-8">{children}</main>
      </div>
    </div>
  );
}
